/**
 * KAN-702: форма добавления материала оператора. Без тега <form>: блок
 * живёт внутри формы инструкций оператора, вложенные формы недопустимы.
 * KAN-721: картинки и документы (PDF, Excel, Word) до 25 МБ, флажок
 * "Основной прайс-лист".
 */
import { useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AttachToEmailCheckbox } from './AttachToEmailCheckbox';
import { MaterialFlagCheckbox } from './MaterialFlagCheckbox';
import { MATERIAL_ACCEPT, validateMaterialFile } from './materialFiles';

export interface NewAttachment {
  file: File;
  title: string;
  description: string;
  attachToEmail: boolean;
  isMainPrice: boolean;
}
interface AddInstructionAttachmentFormProps {
  isSaving: boolean;
  onSave: (data: NewAttachment) => Promise<unknown>;
}

export function AddInstructionAttachmentForm({
  isSaving,
  onSave,
}: AddInstructionAttachmentFormProps): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [attachToEmail, setAttachToEmail] = useState(false);
  const [isMainPrice, setIsMainPrice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    setFile(picked);
    setError(picked ? validateMaterialFile(picked) : null);
  };

  const handleSave = async () => {
    const fileError = validateMaterialFile(file);
    if (fileError || !file) {
      setError(fileError);
      return;
    }
    if (!title.trim()) {
      setError('Укажите название');
      return;
    }
    setError(null);
    try {
      await onSave({
        file,
        title: title.trim(),
        description: description.trim(),
        attachToEmail,
        isMainPrice,
      });
    } catch {
      return; // ошибку показывает toast в мутации
    }
    setFile(null);
    setTitle('');
    setDescription('');
    setAttachToEmail(false);
    setIsMainPrice(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div data-testid="add-attachment-form" className="space-y-2 rounded-md border border-dashed p-3">
      <p className="text-sm font-medium">Добавить материал</p>
      <div className="space-y-1">
        <Label htmlFor="attachment-file">Файл (JPG, PNG, PDF, Excel, Word, до 25 МБ)</Label>
        <Input
          id="attachment-file"
          ref={fileInputRef}
          type="file"
          accept={MATERIAL_ACCEPT}
          onChange={handleFileChange}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="attachment-title">Название</Label>
        <Input
          id="attachment-title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Прайс"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="attachment-description">Описание</Label>
        <Textarea
          id="attachment-description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Что это и когда отправлять: например, актуальный прайс, отправлять, когда клиент спрашивает цены"
          rows={3}
        />
      </div>
      <AttachToEmailCheckbox
        id="attachment-attach-to-email"
        checked={attachToEmail}
        onChange={setAttachToEmail}
      />
      <MaterialFlagCheckbox
        id="attachment-main-price"
        label="Основной прайс-лист"
        hint="бот отправит его на вопросы о ценах; основной прайс у оператора один"
        checked={isMainPrice}
        onChange={setIsMainPrice}
      />
      {error && (
        <p data-testid="attachment-form-error" role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      <Button type="button" size="sm" onClick={handleSave} disabled={isSaving}>
        {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Сохранить материал
      </Button>
    </div>
  );
}
